import React, { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { Recipe } from '@/types';
import { RecipeService } from '@/services/RecipeService';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n/I18nContext';
import { getBrewfatherCredentials, clearBrewfatherCredentialsCache } from '@/services/BrewfatherService';
import RecipeCard from '@/components/RecipeCard';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import SyncIcon from '@mui/icons-material/Sync';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const RecipesPage: React.FC = () => {
	const navigate = useNavigate();
	const { user, getAuthHeader, getUserId } = useAuth();
	const { t } = useI18n();
	const [recipes, setRecipes] = useState<Recipe[]>([]);
	const [loading, setLoading] = useState(true);
	const [syncing, setSyncing] = useState(false);
	const [hasCredentials, setHasCredentials] = useState(false);

	useEffect(() => {
		if (!user) {
			navigate('/login');
			return;
		}
		loadRecipes();
	}, [user]);

	const loadRecipes = async () => {
		try {
			setLoading(true);
			clearBrewfatherCredentialsCache();
			const userId = getUserId();
			if (!userId) {
				setRecipes([]);
				return;
			}
			const authHeader = getAuthHeader();
			const credentials = await getBrewfatherCredentials(userId, authHeader);
			if (!credentials) {
				setRecipes([]);
				return;
			}
			const response = await RecipeService.listRecipes(userId, authHeader);
			setRecipes(response.items || []);
		} catch (error: any) {
			console.error('Error loading recipes:', error);
			toast.error(error.message || t.recipes.errorLoading);
			setRecipes([]);
		} finally {
			setLoading(false);
		}
	};

	const handleSyncBrewfather = async () => {
		try {
			setSyncing(true);
			await loadRecipes();
			toast.success(t.recipes.syncSuccess);
		} catch (error: any) {
			console.error('Error syncing recipes:', error);
			toast.error(error.message || t.recipes.errorSyncing);
		} finally {
			setSyncing(false);
		}
	};

	useEffect(() => {
		const checkCredentials = async () => {
			const userId = getUserId();
			if (!userId) {
				setHasCredentials(false);
				return;
			}
			try {
				const authHeader = getAuthHeader();
				const credentials = await getBrewfatherCredentials(userId, authHeader);
				setHasCredentials(Boolean(credentials));
			} catch {
				setHasCredentials(false);
			}
		};
		checkCredentials();
	}, [user, getUserId, getAuthHeader]);

	if (loading) {
		return (
			<Container sx={{ py: 8, textAlign: 'center' }}>
				<CircularProgress />
			</Container>
		);
	}

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<ToastContainer position="top-right" />
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
				<Typography variant="h4" fontWeight={800}>
					{t.recipes.title}
				</Typography>
				{hasCredentials && (
					<Button
						variant="outlined"
						startIcon={<SyncIcon />}
						onClick={handleSyncBrewfather}
						disabled={syncing}
					>
						{syncing ? t.recipes.syncing : t.recipes.syncBrewfather}
					</Button>
				)}
			</Box>

			{!hasCredentials ? (
				<Paper sx={{ p: 4, textAlign: 'center' }}>
					<Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
						{t.recipes.noCredentials}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						{t.recipes.noCredentialsDescription}
					</Typography>
					<Button variant="contained" onClick={() => navigate('/dashboard')}>
						{t.recipes.goToDashboard}
					</Button>
				</Paper>
			) : recipes.length === 0 ? (
				<Paper sx={{ p: 4, textAlign: 'center' }}>
					<Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
						{t.recipes.noRecipes}
					</Typography>
					<Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSyncBrewfather}>
						{t.recipes.syncBrewfather}
					</Button>
				</Paper>
			) : (
				<Grid container spacing={3}>
					{recipes.map((recipe) => (
						<Grid item xs={12} sm={6} md={4} key={recipe.id}>
							<RecipeCard
								recipe={recipe}
								onClick={() => navigate(`/recipes/${recipe.id}`)}
							/>
						</Grid>
					))}
				</Grid>
			)}
		</Container>
	);
};

export default RecipesPage;
